/** Small pieces shared by the partner screens. */
import { useState, type ReactNode } from 'react'

import { Button } from '@/components/ui/Button'
import { FieldShell } from '@/components/ui/Field'
import { cn } from '@/lib/utils'

import { copyText, generatePassword } from './format'

export interface Figure {
  key: string
  label: string
  value: ReactNode
  hint?: ReactNode
  tone?: 'neutral' | 'success' | 'warning' | 'brand' | 'danger'
}

const TONE: Record<NonNullable<Figure['tone']>, string> = {
  neutral: 'text-ink',
  success: 'text-success',
  warning: 'text-warning',
  brand: 'text-brand',
  danger: 'text-danger',
}

/** A row of labelled figures in one panel (money does not belong in a proportional bar). */
export function FigureStrip({ figures, loading, className }: { figures: Figure[]; loading?: boolean; className?: string }) {
  const cols = figures.length <= 4 ? 'grid-cols-2 lg:grid-cols-4' : figures.length === 5 ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5' : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-6'
  return (
    <div className={cn('panel grid overflow-hidden', cols, className)}>
      {figures.map((figure) => (
        <div key={figure.key} className="min-w-0 border-b border-r border-line px-4 py-3 sm:px-5 [margin-bottom:-1px] [margin-right:-1px]">
          <p className="truncate text-[12px] font-medium text-ink-muted">{figure.label}</p>
          <p className={cn('num mt-1 truncate text-[20px] font-semibold leading-tight sm:text-[22px]', loading ? 'text-ink-faint' : TONE[figure.tone ?? 'neutral'])}>
            {loading ? '–' : figure.value}
          </p>
          {figure.hint && <p className="mt-0.5 truncate text-[11.5px] text-ink-faint">{figure.hint}</p>}
        </div>
      ))}
    </div>
  )
}

/** The four cards the partner sees on their own dashboard, in the same order. */
export function HeadlineCards({ cards }: { cards: Figure[] }) {
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {cards.map((card) => (
        <div key={card.key} className="card min-w-0 px-4 py-3.5 sm:px-5 sm:py-4">
          <p className="truncate text-[13px] font-medium text-ink-muted">{card.label}</p>
          <p className={cn('num mt-1.5 truncate text-[22px] font-semibold leading-none sm:text-[26px]', TONE[card.tone ?? 'neutral'])}>{card.value}</p>
          {card.hint && <p className="mt-2 truncate text-[12px] text-ink-faint">{card.hint}</p>}
        </div>
      ))}
    </div>
  )
}

/** Pills that pick one value (filters, metrics). */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
  size = 'md',
}: {
  options: Array<{ value: T; label: ReactNode }>
  value: T
  onChange: (value: T) => void
  label: string
  size?: 'sm' | 'md'
}) {
  return (
    <div role="radiogroup" aria-label={label} className="hscroll gap-1.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          onClick={() => onChange(option.value)}
          className={cn(
            'shrink-0 rounded-pill border font-medium transition-colors',
            size === 'sm' ? 'h-8 px-3 text-[12.5px]' : 'h-9 px-3.5 text-[13px]',
            value === option.value ? 'border-ink bg-ink text-oncolor' : 'border-line bg-surface text-ink-soft hover:bg-surface-muted',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

/** A password box with Generate and Copy. */
export function PasswordInput({
  value,
  onChange,
  label = 'Password',
  hint,
  error,
  required,
}: {
  value: string
  onChange: (value: string) => void
  label?: string
  hint?: ReactNode
  error?: string | null
  required?: boolean
}) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    if (!value) return
    const ok = await copyText(value)
    setCopied(ok)
    if (ok) window.setTimeout(() => setCopied(false), 1600)
  }
  return (
    <FieldShell label={label} hint={hint} error={error} required={required} htmlFor="partner-password">
      <div className="flex gap-2">
        <input
          id="partner-password"
          type="text"
          autoComplete="new-password"
          spellCheck={false}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className={cn('field-box num min-w-0 flex-1 text-[14px]', error && 'border-danger')}
          placeholder="At least 12 characters"
        />
        <Button variant="secondary" size="sm" className="h-[46px] shrink-0" onClick={() => onChange(generatePassword())}>
          Generate
        </Button>
        <Button variant="ghost" size="sm" className="h-[46px] shrink-0" disabled={!value} onClick={() => void copy()}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </FieldShell>
  )
}

export const TH = 'whitespace-nowrap px-3 py-2.5 font-semibold first:pl-5 last:pr-5'
export const TD = 'px-3 py-3 first:pl-5 last:pr-5'
export const THEAD_ROW = 'border-b border-line text-[11.5px] font-semibold uppercase tracking-[0.08em] text-ink-muted'

/** A table that scrolls sideways on narrow screens instead of squeezing. */
export function TableScroll({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('overflow-x-auto', className)}>{children}</div>
}
